/* eslint-disable react-hooks/set-state-in-effect */
import { useState, useEffect, useCallback, useRef } from 'react'
import { kanbify } from '../lib/kanbifyClient'

function validNotifications(rows = []) {
    return rows
        .filter((notification) => notification?.id)
        .map((notification) => ({
            ...notification,
            title: notification.title || 'Notificare',
            body: notification.body || notification.message || '',
            icon: notification.icon || '!',
        }))
}

function parseMaybeJson(value) {
    if (!value || typeof value !== 'string') return value || {}
    try {
        return JSON.parse(value)
    } catch {
        return {}
    }
}

function describeHistoryAction(row, listName = new Map()) {
    const fromState = parseMaybeJson(row.from_state)
    const toState = parseMaybeJson(row.to_state)
    const metadata = parseMaybeJson(row.metadata)
    const nextList = listName.get(toState?.list_id) || metadata?.to_list_name || metadata?.list_name

    switch (row.action) {
    case 'created':
    case 'INSERT':
        return 'Card creat'
    case 'completed':
        return 'Marcat finalizat'
    case 'reopened':
        return 'Redeschis'
    case 'deleted':
    case 'DELETE':
        return 'Card șters'
    case 'claimed':
        return 'Preluat din pool'
    case 'unclaimed':
        return 'Repus în pool'
    case 'started':
    case 'timer_started':
        return 'Timer pornit'
    case 'completed_timer':
    case 'timer_stopped':
        return 'Timer oprit'
    case 'moved_list':
        return `Mutat în lista ${nextList || 'nouă'}`
    default:
        break
    }

    const parts = []
    if ((fromState?.title || toState?.title) && fromState?.title !== toState?.title) {
        parts.push(`Titlu actualizat`)
    }
    if ((fromState?.description || toState?.description) && fromState?.description !== toState?.description) {
        parts.push('Descriere actualizată')
    }
    if ((fromState?.priority || toState?.priority) && fromState?.priority !== toState?.priority) {
        parts.push('Prioritate actualizată')
    }
    if ((fromState?.due_date || toState?.due_date) && fromState?.due_date !== toState?.due_date) {
        parts.push('Termen actualizat')
    }
    if (fromState?.list_id !== toState?.list_id && (fromState?.list_id || toState?.list_id)) {
        parts.push(`Mutat în ${nextList || 'altă listă'}`)
    }
    return parts.length ? parts.join(' · ') : 'Modificat'
}

export function useNotifications(userId) {
    const [notifications, setNotifications] = useState([])
    const [unreadCount, setUnreadCount] = useState(0)
    const lastSignatureRef = useRef('')

    const replaceNotifications = useCallback((rows = []) => {
        const next = validNotifications(rows)
        const signature = next
            .map((notification) => `${notification.id}:${notification.is_read ? 1 : 0}:${notification.created_at || ''}`)
            .join('|')
        if (signature !== lastSignatureRef.current) {
            lastSignatureRef.current = signature
            setNotifications(next)
        }
        setUnreadCount(next.filter((notification) => !notification.is_read).length)
    }, [])

    const fetch = useCallback(async () => {
        if (!userId) {
            lastSignatureRef.current = ''
            setNotifications([])
            setUnreadCount(0)
            return
        }

        // Fetch fewer rows on initial load (12 covers most users' bell view).
        // Realtime will push new ones; "load more" pattern can be added later
        // if anyone needs the deep history.
        const { data } = await kanbify
            .from('notifications')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false })
            .limit(12)

        replaceNotifications(data || [])
    }, [replaceNotifications, userId])

    useEffect(() => {
        fetch()
    }, [fetch])

    useEffect(() => {
        if (!userId) return undefined
        const id = window.setInterval(() => { void fetch() }, 4000)
        return () => window.clearInterval(id)
    }, [fetch, userId])

    useEffect(() => {
        if (!userId) return undefined

        const channel = kanbify
            .channel(`notifications:${userId}`)
            .on(
                'postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
                () => { void fetch() },
            )
            .subscribe()

        return () => {
            kanbify.removeChannel(channel)
        }
    }, [fetch, userId])

    const markRead = async (id) => {
        if (!id) return
        await kanbify.from('notifications').update({ is_read: true }).eq('id', id)
        setNotifications((current) => current.map((notification) => (
            notification.id === id ? { ...notification, is_read: true } : notification
        )))
        setUnreadCount((count) => Math.max(0, count - 1))
    }

    const markAllRead = async () => {
        if (!userId) return
        await kanbify.from('notifications').update({ is_read: true }).eq('user_id', userId).eq('is_read', false)
        setNotifications((current) => current.map((notification) => ({ ...notification, is_read: true })))
        setUnreadCount(0)
    }

    const deleteNotification = async (id) => {
        if (!id) return
        await kanbify.from('notifications').delete().eq('id', id)
        setNotifications((current) => {
            const updated = current.filter((notification) => notification.id !== id)
            setUnreadCount(updated.filter((notification) => !notification.is_read).length)
            return updated
        })
    }

    const deleteAll = async () => {
        if (!userId) return
        await kanbify.from('notifications').delete().eq('user_id', userId)
        setNotifications([])
        setUnreadCount(0)
    }

    return { notifications, unreadCount, fetch, markRead, markAllRead, deleteNotification, deleteAll }
}

export function useActivityHistory({ companyId, rangeStart, rangeEnd, filterUserId }) {
    const [events, setEvents] = useState([])
    const [loading, setLoading] = useState(true)

    const fetch = useCallback(async () => {
        if (!companyId || !rangeStart || !rangeEnd) {
            setEvents([])
            setLoading(false)
            return
        }

        setLoading(true)

        const safe = async (promise) => {
            try {
                const { data, error } = await promise
                if (error) return []
                return data || []
            } catch {
                return []
            }
        }

        let query = kanbify
            .from('v_activity_history')
            .select('*')
            .eq('company_id', companyId)
            .gte('event_at', rangeStart)
            .lte('event_at', rangeEnd)
            .order('event_at', { ascending: false })

        // Admin with filter: show only that user's events
        // Admin without filter: show all company events
        // Non-admin: show all company events (RLS already scopes by company)
        // The old filter eq('user_id', userId) was too restrictive —
        // employees couldn't see ANY activity if they hadn't personally acted
        if (filterUserId) {
            query = query.eq('user_id', filterUserId)
        }

        const baseEvents = await safe(query)

        let historyQuery = kanbify
            .from('card_history')
            .select('id, card_id, user_id, action, from_state, to_state, metadata, created_at')
            .gte('created_at', rangeStart)
            .lte('created_at', rangeEnd)
            .order('created_at', { ascending: false })
            .limit(400)
        let commentsQuery = kanbify
            .from('comments')
            .select('id, card_id, user_id, text, created_at')
            .gte('created_at', rangeStart)
            .lte('created_at', rangeEnd)
            .order('created_at', { ascending: false })
            .limit(250)
        let attachmentsQuery = kanbify
            .from('attachments')
            .select('id, card_id, created_by, name, created_at')
            .gte('created_at', rangeStart)
            .lte('created_at', rangeEnd)
            .order('created_at', { ascending: false })
            .limit(250)

        if (filterUserId) {
            historyQuery = historyQuery.eq('user_id', filterUserId)
            commentsQuery = commentsQuery.eq('user_id', filterUserId)
            attachmentsQuery = attachmentsQuery.eq('created_by', filterUserId)
        }

        const [historyRows, commentRows, attachmentRows] = await Promise.all([
            safe(historyQuery),
            safe(commentsQuery),
            safe(attachmentsQuery),
        ])

        const cardIds = Array.from(new Set([
            ...historyRows.map((row) => row.card_id),
            ...commentRows.map((row) => row.card_id),
            ...attachmentRows.map((row) => row.card_id),
        ].filter(Boolean)))

        const userIds = Array.from(new Set([
            ...historyRows.map((row) => row.user_id),
            ...commentRows.map((row) => row.user_id),
            ...attachmentRows.map((row) => row.created_by),
        ].filter(Boolean)))

        const [cardRows, userRows] = await Promise.all([
            cardIds.length
                ? safe(kanbify.from('cards').select('id, title, list_id').in('id', cardIds))
                : [],
            userIds.length
                ? safe(kanbify.from('profiles').select('id, full_name, avatar_url').in('id', userIds))
                : [],
        ])

        const listIds = Array.from(new Set(cardRows.map((card) => card.list_id).filter(Boolean)))
        const listRows = listIds.length
            ? await safe(kanbify.from('lists').select('id, name, board_id').in('id', listIds))
            : []
        const boardIds = Array.from(new Set(listRows.map((list) => list.board_id).filter(Boolean)))
        const boardRows = boardIds.length
            ? await safe(kanbify.from('boards').select('id, name, company_id').in('id', boardIds))
            : []

        const boardsById = new Map(boardRows.map((board) => [board.id, board]))
        const listsById = new Map(listRows.map((list) => [list.id, list]))
        const usersById = new Map(userRows.map((profile) => [profile.id, profile]))
        const listName = new Map(listRows.map((list) => [list.id, list.name]))
        const cardsById = new Map()
        for (const card of cardRows) {
            const list = listsById.get(card.list_id)
            const board = list ? boardsById.get(list.board_id) : null
            if (String(board?.company_id || '') !== String(companyId)) continue
            cardsById.set(card.id, { ...card, list, board })
        }

        const mkEvent = ({ id, event_type, card_id, user_id, event_at, detail_text, duration_minutes = 0 }) => {
            const profile = usersById.get(user_id)
            const card = cardsById.get(card_id)
            if (!card) return null
            return {
                id,
                event_type,
                company_id: companyId,
                card_id,
                user_id: user_id || null,
                event_at,
                duration_minutes,
                detail_text,
                card_title: card.title,
                board_name: card.board?.name || null,
                list_name: card.list?.name || null,
                user_name: profile?.full_name || null,
                avatar_url: profile?.avatar_url || null,
            }
        }

        const extraEvents = [
            ...historyRows.map((row) => mkEvent({
                id: `history-${row.id}`,
                event_type: 'snapshot',
                card_id: row.card_id,
                user_id: row.user_id,
                event_at: row.created_at,
                detail_text: describeHistoryAction(row, listName),
            })),
            ...commentRows.map((row) => mkEvent({
                id: `comment-${row.id}`,
                event_type: 'comment',
                card_id: row.card_id,
                user_id: row.user_id,
                event_at: row.created_at,
                detail_text: `Comentariu: ${String(row.text || '').slice(0, 100)}`,
            })),
            ...attachmentRows.map((row) => mkEvent({
                id: `attachment-${row.id}`,
                event_type: 'attachment_added',
                card_id: row.card_id,
                user_id: row.created_by,
                event_at: row.created_at,
                detail_text: `Fișier atașat: ${String(row.name || 'fișier').slice(0, 80)}`,
            })),
        ].filter(Boolean)

        const byId = new Map()
        for (const event of [...baseEvents, ...extraEvents]) {
            if (!event?.id || !event?.event_at) continue
            byId.set(String(event.id), event)
        }
        setEvents(Array.from(byId.values()).sort((a, b) => new Date(b.event_at) - new Date(a.event_at)))
        setLoading(false)
    }, [companyId, filterUserId, rangeEnd, rangeStart])

    useEffect(() => {
        fetch()
    }, [fetch])

    return { events, loading, refetch: fetch }
}
